import { useEffect, useRef } from 'react';

/**
 * Desplaza una pista horizontal en bucle continuo con requestAnimationFrame.
 * La pista debe contener su contenido duplicado (2 copias) para que el bucle no tenga cortes.
 * Funciona en todos los navegadores móviles (no depende de animaciones CSS ni de :hover).
 */
export function useMarquee<T extends HTMLElement>(pxPerSecond = 40) {
  const trackRef = useRef<T>(null);
  const paused = useRef(false);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let offset = 0;
    let last = performance.now();
    let frame = 0;

    const tick = (now: number) => {
      const dt = Math.min(now - last, 100); // evita saltos al volver de otra pestaña
      last = now;
      const half = track.scrollWidth / 2;
      if (!paused.current && half > 0) {
        offset = (offset + (pxPerSecond * dt) / 1000) % half;
        track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    const pause = () => (paused.current = true);
    const resume = () => (paused.current = false);
    const onPointerEnter = (e: PointerEvent) => e.pointerType === 'mouse' && pause();
    const onPointerLeave = (e: PointerEvent) => e.pointerType === 'mouse' && resume();

    track.addEventListener('pointerenter', onPointerEnter);
    track.addEventListener('pointerleave', onPointerLeave);
    track.addEventListener('touchstart', pause, { passive: true });
    track.addEventListener('touchend', resume);
    track.addEventListener('touchcancel', resume);

    return () => {
      cancelAnimationFrame(frame);
      track.removeEventListener('pointerenter', onPointerEnter);
      track.removeEventListener('pointerleave', onPointerLeave);
      track.removeEventListener('touchstart', pause);
      track.removeEventListener('touchend', resume);
      track.removeEventListener('touchcancel', resume);
    };
  }, [pxPerSecond]);

  return trackRef;
}
